import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormDocumentFetcher } from '../FormDocumentFetcherContext';
import { useFormUpload } from '../FormUploadContext';
import { parseFileEntries } from '../helpers';
import { DocumentPendingReviewError } from '../../services/documentApi';
import type { FormFileEntry } from '../types';

/**
 * Local picker entry state. Carries the canonical IDs (which become the form
 * value), plus transient UI state for the four lifecycle stages:
 *
 *   1. `staged`     — file selected but not yet uploaded. The user can remove
 *                     or replace it freely. Form submit triggers the upload.
 *   2. `uploading`  — upload in flight (during the form's submit pass, or via
 *                     explicit retry).
 *   3. committed    — IDs populated, no `staged`/`uploading`/`error` flags.
 *                     This is the only state that contributes to form value.
 *   4. `error`      — last upload attempt failed; user can retry/remove.
 */
interface FilePickerEntry {
  /** Stable key for React lists — assigned client-side, not persisted. */
  localKey: string;
  document_submission_id: number;
  document_version_id: number;
  filename?: string;
  mime_type?: string;
  /** Failure message; non-empty blocks submit (validator picks this up). */
  error?: string;
  /** Set when the server's `409` carried an existing pending submission id. */
  pending_submission_id?: number;
  /** True while the upload is in flight. */
  uploading?: boolean;
  /**
   * True for entries the user has attached but not yet uploaded. Uploads
   * are deferred to form submit so the user can remove or replace files
   * before they hit the server.
   */
  staged?: boolean;
  /** Abort handle so we can cancel inflight uploads when removed. */
  abort?: AbortController;
  /** Held while the upload is staged / pending / failed; cleared on success. */
  file?: File;
  /** Lazily resolved presigned URL for preview / "Open" links. */
  resolvedUrl?: string;
  /** Epoch ms after which the resolved URL is no longer safe to use. */
  urlExpiresAt?: number;
  /**
   * Local blob: URL for previewing a staged file before it uploads. Created
   * via `URL.createObjectURL(file)` and revoked on remove / replace /
   * unmount. Lets the candidate verify what they attached without waiting
   * for the server round-trip.
   */
  localPreviewUrl?: string;
}

interface UseFilePickerStateArgs {
  value: unknown;
  onChange: (next: unknown) => void;
  maxFiles: number;
  allowMultiple: boolean;
  /**
   * Backend document-type tag sourced from `field.document_type_id`.
   * Forwarded to the bound upload function; required by the versioned API.
   */
  documentTypeId?: number;
}

/**
 * Convert internal picker entries into the form value shape. The picker
 * always emits an array; the DynamicForm submit step is responsible for
 * unwrapping single-file fields to a single object per the backend's
 * documented contract.
 *
 * Staged and uploading entries are excluded — they have no IDs yet so they
 * can't be serialised. The validator sees an empty value and surfaces the
 * "required" error if applicable; the DynamicForm submit pass uploads
 * staged entries first, then re-runs validation.
 */
const toFormValue = (entries: FilePickerEntry[]): FormFileEntry[] =>
  entries
    .filter((e) => !e.uploading && !e.staged)
    .map((e) => {
      const out: FormFileEntry = {
        document_submission_id: e.document_submission_id,
        document_version_id: e.document_version_id,
      };
      if (e.filename) out.filename = e.filename;
      if (e.mime_type) out.mime_type = e.mime_type;
      if (e.error) out.error = e.error;
      if (e.pending_submission_id) out.pending_submission_id = e.pending_submission_id;
      return out;
    });

let counter = 0;
const nextKey = () => `file-${++counter}`;

/** Safe wrappers — jsdom and SSR environments don't implement these. */
const createBlobUrl = (file: File): string | undefined => {
  if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return undefined;
  try {
    return URL.createObjectURL(file);
  } catch {
    return undefined;
  }
};

const revokeBlobUrl = (url: string): void => {
  if (typeof URL === 'undefined' || typeof URL.revokeObjectURL !== 'function') return;
  try {
    URL.revokeObjectURL(url);
  } catch {
    // ignore — bad URL or environment without blob support
  }
};

/** Refresh a presigned URL ~10% before its TTL elapses to avoid flapping. */
const URL_REFRESH_LEEWAY_MS = 10_000;

/**
 * Shared picker state for `versioned_image_picker` and `versioned_pdf_picker`.
 * Owns:
 *   - the local entry list (uploading / failed / success / pending-review)
 *   - the form value sync (always an array; DynamicForm unwraps as needed)
 *   - per-file upload + cancel + retry mechanics
 *   - lazy resolution of preview URLs via the document fetcher context
 */
export const useFilePickerState = ({
  value,
  onChange,
  maxFiles,
  allowMultiple,
  documentTypeId,
}: UseFilePickerStateArgs) => {
  const uploadFile = useFormUpload();
  const fetchDocument = useFormDocumentFetcher();

  // Hydrate from the incoming form value. Lazy initializer covers the
  // mount-time case; an effect below catches the case where the value
  // arrives async (e.g. existing-submission prefill resolves after mount).
  const [entries, setEntries] = useState<FilePickerEntry[]>(() =>
    parseFileEntries(value).map((e) => ({
      localKey: nextKey(),
      document_submission_id: e.document_submission_id,
      document_version_id: e.document_version_id,
      filename: e.filename,
      mime_type: e.mime_type,
      error: e.error,
      pending_submission_id: e.pending_submission_id,
    })),
  );

  // Keep a ref to the latest entries so async callbacks see fresh state
  // without re-creating closures on every render.
  const entriesRef = useRef(entries);
  entriesRef.current = entries;

  // Async hydration: when the form value gains entries while ours is empty
  // (the canonical "prefill arrived after mount" case), pull them in.
  // Bounded by `entries.length === 0` so user-driven edits are never
  // clobbered, and so the entries → value → entries round-trip below
  // doesn't loop.
  useEffect(() => {
    if (entriesRef.current.length > 0) return;
    const parsed = parseFileEntries(value);
    if (parsed.length === 0) return;
    setEntries(
      parsed.map((e) => ({
        localKey: nextKey(),
        document_submission_id: e.document_submission_id,
        document_version_id: e.document_version_id,
        filename: e.filename,
        mime_type: e.mime_type,
        error: e.error,
        pending_submission_id: e.pending_submission_id,
      })),
    );
  }, [value]);

  // Whenever the local entries change, push the form-shape value upstream.
  // Skip the initial render since we just hydrated *from* `value`.
  const didInitRef = useRef(false);
  useEffect(() => {
    if (!didInitRef.current) {
      didInitRef.current = true;
      return;
    }
    onChange(toFormValue(entries));
    // We intentionally don't include `onChange` in deps — DynamicForm passes a
    // new function every render, which would cause an infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries]);

  // Lazy preview-URL resolution. For every successful entry without a fresh
  // resolved URL, kick off a fetch through the document fetcher (if one is
  // configured). Refreshes near TTL expiry too.
  const inflightFetchesRef = useRef<Map<number, AbortController>>(new Map());
  useEffect(() => {
    if (!fetchDocument) return;
    const now = Date.now();
    for (const entry of entries) {
      if (entry.error || entry.uploading) continue;
      if (!entry.document_version_id) continue;
      const expiresAt = entry.urlExpiresAt;
      const needsFetch =
        !entry.resolvedUrl ||
        (typeof expiresAt === 'number' && expiresAt - URL_REFRESH_LEEWAY_MS <= now);
      if (!needsFetch) continue;
      if (inflightFetchesRef.current.has(entry.document_version_id)) continue;

      const controller = new AbortController();
      inflightFetchesRef.current.set(entry.document_version_id, controller);
      fetchDocument(entry.document_version_id, { signal: controller.signal })
        .then((details) => {
          inflightFetchesRef.current.delete(entry.document_version_id);
          if (controller.signal.aborted) return;
          setEntries((prev) =>
            prev.map((e) =>
              e.document_version_id === entry.document_version_id && !e.uploading && !e.error
                ? {
                    ...e,
                    resolvedUrl: details.url,
                    urlExpiresAt: Date.now() + details.ttl_seconds * 1000,
                    mime_type: e.mime_type || details.mimetype,
                  }
                : e,
            ),
          );
        })
        .catch((err) => {
          inflightFetchesRef.current.delete(entry.document_version_id);
          if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
          // Preview failures shouldn't block submit — leave entry without
          // resolvedUrl; the picker renders a generic "preview unavailable" cell.
        });
    }
    // We deliberately re-run when the entries shape changes. The map of
    // inflight controllers de-dupes within a render pass.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries, fetchDocument]);

  // Revoke any outstanding local preview URLs on unmount so blob memory is
  // released even if the user closes the form mid-staging.
  useEffect(() => {
    return () => {
      for (const e of entriesRef.current) {
        if (e.localPreviewUrl) revokeBlobUrl(e.localPreviewUrl);
      }
    };
  }, []);

  useEffect(() => {
    const map = inflightFetchesRef.current;
    return () => {
      for (const ctrl of map.values()) ctrl.abort();
      map.clear();
    };
  }, []);

  /**
   * Stage selected files locally without uploading. Uploads are deferred to
   * form submit (via `uploadPending`) so the user can remove or replace
   * files before they hit the server.
   *
   * Each staged file gets a `blob:` URL via `URL.createObjectURL` so the
   * picker can show a real preview (image thumbnail or PDF "open" link)
   * straight away. Those URLs are tracked and revoked on remove / single-
   * mode replace / unmount.
   */
  const addFiles = (files: FileList | File[]) => {
    if (!uploadFile) return; // disabled state — see component
    const filesArr = Array.from(files);
    if (filesArr.length === 0) return;

    const currentCount = entriesRef.current.filter((e) => !e.error).length;
    const room = Math.max(0, maxFiles - currentCount);
    const slice = allowMultiple ? filesArr.slice(0, room) : filesArr.slice(0, 1);
    if (slice.length === 0) return;

    const newEntries: FilePickerEntry[] = slice.map((file) => ({
      localKey: nextKey(),
      document_submission_id: 0,
      document_version_id: 0,
      filename: file.name,
      mime_type: file.type || undefined,
      staged: true,
      file,
      localPreviewUrl: createBlobUrl(file),
    }));

    if (!allowMultiple) {
      // Single mode: replace the current entry. Abort any inflight upload
      // and revoke the previous preview URL to avoid leaking blob memory.
      setEntries((prev) => {
        prev.forEach((e) => {
          e.abort?.abort();
          if (e.localPreviewUrl) revokeBlobUrl(e.localPreviewUrl);
        });
        return newEntries;
      });
    } else {
      setEntries((prev) => [...prev, ...newEntries]);
    }
  };

  type UploadOutcome =
    | { kind: 'success'; uploaded: { document_submission_id: number; document_version_id: number; filename?: string; mime_type?: string } }
    | { kind: 'cancelled' }
    | { kind: 'error' };

  /**
   * Upload one entry (staged or previously errored). Used internally by
   * `uploadPending` for the submit pass, and exposed via `retry` for
   * manual recovery from a per-entry failure.
   *
   * Returns the outcome inline so callers can build the post-upload form
   * value without waiting for React state to flush.
   */
  const uploadOneEntry = useCallback(
    async (localKey: string): Promise<UploadOutcome> => {
      if (!uploadFile) return { kind: 'error' };
      const target = entriesRef.current.find((e) => e.localKey === localKey);
      if (!target?.file) return { kind: 'error' };
      const file = target.file;
      const abort = new AbortController();
      setEntries((prev) =>
        prev.map((e) =>
          e.localKey === localKey
            ? {
                ...e,
                staged: false,
                uploading: true,
                error: undefined,
                pending_submission_id: undefined,
                abort,
              }
            : e,
        ),
      );
      try {
        const uploaded = await uploadFile(file, { documentTypeId, signal: abort.signal });
        setEntries((prev) =>
          prev.map((e) =>
            e.localKey === localKey
              ? {
                  ...e,
                  document_submission_id: uploaded.document_submission_id,
                  document_version_id: uploaded.document_version_id,
                  filename: uploaded.filename || file.name,
                  mime_type: uploaded.mime_type || e.mime_type,
                  uploading: false,
                  staged: false,
                  error: undefined,
                  pending_submission_id: undefined,
                  file: undefined,
                  abort: undefined,
                }
              : e,
          ),
        );
        return {
          kind: 'success',
          uploaded: {
            document_submission_id: uploaded.document_submission_id,
            document_version_id: uploaded.document_version_id,
            filename: uploaded.filename || file.name,
            mime_type: uploaded.mime_type,
          },
        };
      } catch (err: any) {
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
          // The remove-while-uploading path already dropped the entry.
          return { kind: 'cancelled' };
        }
        const isPending = err instanceof DocumentPendingReviewError;
        setEntries((prev) =>
          prev.map((e) =>
            e.localKey === localKey
              ? {
                  ...e,
                  uploading: false,
                  staged: false,
                  error: err?.message || 'Upload failed',
                  pending_submission_id: isPending
                    ? (err as DocumentPendingReviewError).pendingSubmissionId
                    : undefined,
                  abort: undefined,
                }
              : e,
          ),
        );
        return { kind: 'error' };
      }
    },
    [documentTypeId, uploadFile],
  );

  /**
   * Upload every staged entry. Called by the form's submit pass via the
   * pending-uploads registry. Returns the post-upload form value built
   * from a captured start snapshot + in-memory upload results, so the
   * caller doesn't have to wait for React state to flush before validating.
   */
  const uploadPending = useCallback(async (): Promise<{
    success: boolean;
    value: FormFileEntry[];
  }> => {
    const startSnapshot = entriesRef.current;
    const stagedAtStart = startSnapshot.filter((e) => e.staged && e.file);
    if (stagedAtStart.length === 0) {
      return { success: true, value: toFormValue(startSnapshot) };
    }

    const stagedKeys = new Set(stagedAtStart.map((e) => e.localKey));
    const committedBefore = startSnapshot.filter(
      (e) => !stagedKeys.has(e.localKey) && !e.staged && !e.uploading && !e.error,
    );

    const results = await Promise.all(
      stagedAtStart.map(async (entry) => ({
        entry,
        outcome: await uploadOneEntry(entry.localKey),
      })),
    );

    const allSuccess = results.every(
      ({ outcome }) => outcome.kind === 'success' || outcome.kind === 'cancelled',
    );

    const value: FormFileEntry[] = [];
    for (const e of committedBefore) {
      const out: FormFileEntry = {
        document_submission_id: e.document_submission_id,
        document_version_id: e.document_version_id,
      };
      if (e.filename) out.filename = e.filename;
      if (e.mime_type) out.mime_type = e.mime_type;
      value.push(out);
    }
    for (const { outcome } of results) {
      if (outcome.kind !== 'success') continue;
      const out: FormFileEntry = {
        document_submission_id: outcome.uploaded.document_submission_id,
        document_version_id: outcome.uploaded.document_version_id,
      };
      if (outcome.uploaded.filename) out.filename = outcome.uploaded.filename;
      if (outcome.uploaded.mime_type) out.mime_type = outcome.uploaded.mime_type;
      value.push(out);
    }

    return { success: allSuccess, value };
  }, [uploadOneEntry]);

  const remove = (localKey: string) => {
    setEntries((prev) => {
      const target = prev.find((e) => e.localKey === localKey);
      target?.abort?.abort();
      if (target?.localPreviewUrl) revokeBlobUrl(target.localPreviewUrl);
      return prev.filter((e) => e.localKey !== localKey);
    });
  };

  /** Manual retry of one failed entry (also used by the Retry icon in the UI). */
  const retry = useCallback(
    (localKey: string) => {
      void uploadOneEntry(localKey);
    },
    [uploadOneEntry],
  );

  const counts = useMemo(() => {
    const total = entries.length;
    const uploading = entries.filter((e) => e.uploading).length;
    const failed = entries.filter((e) => !!e.error).length;
    const staged = entries.filter((e) => !!e.staged).length;
    return { total, uploading, failed, staged };
  }, [entries]);

  return {
    entries,
    counts,
    canAddMore: entries.filter((e) => !e.error).length < maxFiles,
    addFiles,
    remove,
    retry,
    uploadPending,
    uploadDisabled: !uploadFile,
  };
};

export type { FilePickerEntry };
